import { Link } from "react-router-dom";
import { Disclaimer } from "@/components/Shared";
import {
  Sparkles, Users, ArrowRight, DollarSign, Clock, Info, PhoneCall,
  CheckCircle2, AlertTriangle, Trophy, Heart, ShieldCheck, Home as HomeIcon,
  Plane, Stethoscope, Wallet, GraduationCap, Handshake, HelpCircle,
} from "lucide-react";

/*
  Data source: Department of Home Affairs (June 2026 pricing table)
  + racc.net.au/parent-visa-australia (last updated 22 June 2026)
*/

// Rich comparison rows for the master table
const TABLE_ROWS = [
  {
    code: "103", name: "Parent Visa",
    type: "Permanent · Non-Contributory",
    location: "Offshore application", pr: true,
    first: "AUD 5,280", second: "AUD 2,065",
    total: "from AUD 7,345 / person",
    processing: "~33 years (new applications)",
    aged: "No — for parents of any age",
    bof: true,
    best: "Lowest cost route to PR — only viable if wait time is acceptable.",
    accent: "from-[#29324A] to-[#5A3A8B]",
  },
  {
    code: "804", name: "Aged Parent Visa",
    type: "Permanent · Non-Contributory",
    location: "Onshore application", pr: true,
    first: "AUD 5,280", second: "AUD 2,065",
    total: "from AUD 7,345 / person",
    processing: "~33 years (new applications)",
    aged: "Yes — Age Pension age (67)",
    bof: true,
    best: "Lets an aged parent stay in Australia on a Bridging Visa while waiting.",
    accent: "from-[#1B1430] to-[#29324A]",
  },
  {
    code: "143", name: "Contributory Parent Visa",
    type: "Permanent · Contributory",
    location: "Offshore application", pr: true,
    first: "AUD 5,040", second: "AUD 43,600 (direct) · AUD 19,420 (from 173)",
    total: "~AUD 48,640 / person",
    processing: "~15 years (new applications)",
    aged: "No — for parents of any age",
    bof: true,
    best: "The most popular pathway — significantly faster processing at higher cost.",
    accent: "from-[#29324A] to-[#1f2638]",
  },
  {
    code: "864", name: "Contributory Aged Parent Visa",
    type: "Permanent · Contributory",
    location: "Onshore application", pr: true,
    first: "AUD 5,040", second: "AUD 43,600 (direct) · AUD 19,420 (from 884)",
    total: "~AUD 48,640 / person",
    processing: "~15 years (new applications)",
    aged: "Yes — Age Pension age (67)",
    bof: true,
    best: "Fastest onshore PR route for parents already at Age Pension age.",
    accent: "from-[#C43A89] to-[#C43A89]",
  },
  {
    code: "173", name: "Contributory Parent (Temporary) Visa",
    type: "Temporary (2 yrs) · Contributory",
    location: "Offshore application", pr: false,
    first: "AUD 3,395", second: "AUD 29,130",
    total: "~AUD 32,525 / person",
    processing: "~15 years (new applications)",
    aged: "No — for parents of any age",
    bof: true,
    best: "Stage 1 of the two-stage 173 → 143 route — splits the total cost over time.",
    accent: "from-[#1B1430] to-[#29324A]",
  },
  {
    code: "884", name: "Contributory Aged Parent (Temporary) Visa",
    type: "Temporary (2 yrs) · Contributory",
    location: "Onshore application", pr: false,
    first: "AUD 5,040", second: "AUD 29,130",
    total: "~AUD 34,170 / person",
    processing: "~15 years (new applications)",
    aged: "Yes — Age Pension age (67)",
    bof: true,
    best: "Stage 1 of the two-stage 884 → 864 route for aged parents onshore.",
    accent: "from-[#1f2638] to-[#C43A89]",
  },
  {
    code: "870", name: "Sponsored Parent (Temporary) Visa",
    type: "Temporary (3 or 5 yrs) · Sponsor income tested",
    location: "Onshore or offshore", pr: false,
    first: "AUD 1,215", second: "AUD 4,855 (3 yr) · AUD 10,925 (5 yr)",
    total: "AUD 6,070 (3 yr) · AUD 12,140 (5 yr)",
    processing: "90% within 10 months (not capped)",
    aged: "No — for parents of any age",
    bof: false,
    best: "By far the fastest way to bring parents for a long continuous stay (up to 10 yrs total).",
    accent: "from-[#29324A] to-[#C43A89]",
  },
  {
    code: "600", name: "Visitor Visa — Sponsored Family Stream",
    type: "Temporary · Visitor",
    location: "Offshore application", pr: false,
    first: "AUD 250", second: "—",
    total: "AUD 250",
    processing: "1–3 months typical",
    aged: "No — for parents of any age",
    bof: false,
    best: "Short family visits up to 12 months — the practical bridge while waiting for a parent visa.",
    accent: "from-[#29324A] to-[#1B1430]",
  },
];

const AFTER_GRANT_RIGHTS = [
  { icon: HomeIcon, k: "Live in Australia", v: "Stay indefinitely with no expiry on residence." },
  { icon: Wallet, k: "Work without restriction", v: "Same work rights as any Australian permanent resident." },
  { icon: Stethoscope, k: "Medicare", v: "Enrol in Australia's public health scheme (Centrelink waiting periods may apply)." },
  { icon: Plane, k: "Travel", v: "Enter and leave Australia freely for 5 years, then apply for a Resident Return Visa (RRV)." },
  { icon: GraduationCap, k: "Study", v: "Access to schools and post-secondary study at domestic rates." },
  { icon: Handshake, k: "Sponsor family", v: "Sponsor eligible family members for their own visas." },
];

const MYTHS = [
  "Assuming all parents automatically qualify — most permanent visas require Balance of Family + Assurance of Support.",
  "Underestimating waiting periods — new non-contributory applications can take ~33 years.",
  "Overlooking the Balance of Family Test — it disqualifies many parents with children spread across countries.",
  "Not budgeting for Assurance of Support — a refundable bond of AUD 10,000+ is held by Centrelink for 10 years.",
  "Confusing onshore & offshore rules — parents under 67 applying onshore don't get a bridging visa.",
];

export default function ParentVisasHub() {
  return (
    <div data-testid="parent-visas-hub" className="bg-white">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-40 -right-40 w-[32rem] h-[32rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Users className="h-3 w-3" /> Australian Parent Visas · Updated June 2026
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-4 max-w-4xl">
            Bringing your <span className="text-[#C43A89]">parents</span> to Australia — every pathway, in one place.
          </h1>
          <p className="text-[15px] md:text-base text-slate-200 leading-relaxed mt-6 max-w-3xl">
            Australia offers <strong className="text-white">8 parent visa options</strong> across two main families —
            <strong className="text-white"> Contributory</strong> (higher cost, faster) and
            <strong className="text-white"> Non-Contributory</strong> (lower cost, very long queue) — plus a Sponsored Parent Temporary visa (870) and a Visitor visa (600). Compare them side-by-side below, understand the two big eligibility rules, then run our Pathway Assessment tool for a personalised recommendation.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/parent-visa-finder"
              data-testid="hub-cta-finder"
              className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4"
            >
              <Sparkles className="h-4 w-4" /> Start Pathway Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/book-consultation"
              data-testid="hub-cta-book"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4"
            >
              <PhoneCall className="h-4 w-4" /> Book AUD $110 consultation
            </Link>
          </div>
        </div>
      </section>

      {/* SUMMARY STRIP */}
      <section className="bg-[#F8F4FB] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-4 gap-6">
          {[
            { icon: Trophy, k: "8", l: "Parent visa subclasses" },
            { icon: Clock, k: "10 mo → 33 yrs", l: "Processing spread" },
            { icon: DollarSign, k: "$250 → $48k+", l: "Cost per applicant" },
            { icon: ShieldCheck, k: "MARN 2619415 · IAA 202603837", l: "Registered Migration Agent & Licensed Immigration Adviser" },
          ].map(({ icon: Icon, k, l }) => (
            <div key={l} className="flex items-start gap-3">
              <span className="shrink-0 h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                <Icon className="h-4 w-4 text-[#29324A]" />
              </span>
              <div>
                <div className="font-display text-lg text-[#29324A] leading-none">{k}</div>
                <div className="text-[11.5px] tracking-wider uppercase text-slate-500 mt-1 font-bold">{l}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT IS A PARENT VISA + Contributory vs Non */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Info className="h-3 w-3" /> The basics
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">What is a Parent Visa?</h2>
          <p className="text-[14.5px] text-slate-700 leading-relaxed mt-4 max-w-3xl">
            A Parent Visa lets the parent of an Australian citizen, permanent resident, or eligible New Zealand citizen come to Australia — either permanently or on a long temporary stay. Places are capped every Migration Program year, so demand far exceeds supply. That&apos;s why waiting times can stretch from months (870) to decades (103/804).
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div className="border border-slate-200 p-6 md:p-7 bg-gradient-to-br from-white to-[#F8F4FB]">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A] flex items-center gap-2">
                <Sparkles className="h-3 w-3" /> Contributory
              </div>
              <h3 className="font-display text-xl text-[#29324A] mt-2">Higher fee · faster (~15 yrs)</h3>
              <p className="text-[13.5px] text-slate-700 leading-relaxed mt-2">
                Subclasses <strong>143, 173, 864, 884</strong>. You pay a much larger second visa application charge (VAC) — currently up to <strong>AUD 43,600</strong> — and in return your application is processed <strong>much faster</strong> than the non-contributory queue. Requires an Assurance of Support bond.
              </p>
              <ul className="mt-3 space-y-1.5 text-[12.5px] text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Two-stage option (173→143 or 884→864) splits the cost</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Full permanent residency &amp; Medicare on grant</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Assurance of Support bond required</li>
              </ul>
            </div>

            <div className="border border-slate-200 p-6 md:p-7 bg-gradient-to-br from-white to-[#F0F1F5]">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A] flex items-center gap-2">
                <Clock className="h-3 w-3" /> Non-Contributory
              </div>
              <h3 className="font-display text-xl text-[#29324A] mt-2">Lower fee · very long queue (~33 yrs)</h3>
              <p className="text-[13.5px] text-slate-700 leading-relaxed mt-2">
                Subclasses <strong>103 &amp; 804</strong>. The base charge is only AUD 5,280 + AUD 2,065 second instalment — but with an estimated <strong>33-year</strong> queue on new applications. Realistic only for long-term family planning.
              </p>
              <ul className="mt-3 space-y-1.5 text-[12.5px] text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Lowest total government cost</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> 804 gives aged parent Bridging Visa A onshore</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" /> Applicant must be 65+ for a realistic outcome within lifetime</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE — THE STAR SECTION */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]" id="compare">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-8">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
              <Trophy className="h-3 w-3" /> Side-by-side comparison
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">All 8 Parent Visa subclasses — at a glance</h2>
            <p className="text-[13.5px] text-slate-600 mt-4 leading-relaxed">
              Fees below are the Department of Home Affairs base application charge per primary applicant (current as at June 2026). Processing times are Department estimates for <em>new</em> applications lodged today and are reviewed annually. Additional applicants (spouse, dependants) attract further charges.
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block bg-white border border-slate-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="parent-visa-comparison-table">
              <thead>
                <tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 py-4 font-display text-[13px] w-[26%]">Subclass &amp; name</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Category &amp; location</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Fees (per applicant)</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Processing</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Aged parent?</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Balance of Family?</th>
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((r, i) => (
                  <tr key={r.code} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100 hover:bg-[#F8F4FB] transition-colors`}>
                    <td className="px-4 py-4 align-top">
                      <div className={`h-1 w-10 bg-gradient-to-r ${r.accent} mb-2`} />
                      <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500">Subclass {r.code}</div>
                      <div className="font-display text-[15px] text-[#29324A] leading-tight mt-0.5">{r.name}</div>
                      <div className="text-[11.5px] text-slate-500 mt-1.5">
                        {r.pr ? <span className="text-emerald-700 font-bold">Permanent residency</span> : <span className="text-amber-700 font-bold">Temporary</span>}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top text-[12.5px] text-slate-700">
                      <div>{r.type}</div>
                      <div className="text-[11px] text-slate-500 mt-1">{r.location}</div>
                    </td>
                    <td className="px-4 py-4 align-top text-[12.5px] text-slate-700">
                      <div><span className="text-slate-500">1st:</span> {r.first}</div>
                      <div className="mt-0.5"><span className="text-slate-500">2nd:</span> {r.second}</div>
                      <div className="mt-1.5 text-[#C43A89] font-bold">{r.total}</div>
                    </td>
                    <td className="px-4 py-4 align-top text-[12.5px] text-slate-700">{r.processing}</td>
                    <td className="px-4 py-4 align-top text-[12.5px] text-slate-700">{r.aged}</td>
                    <td className="px-4 py-4 align-top text-[12.5px]">
                      {r.bof ? (
                        <span className="inline-flex items-center gap-1 text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-bold">
                          <CheckCircle2 className="h-3 w-3" /> Required
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-600 bg-slate-50 border border-slate-200 px-2 py-0.5 text-[11px] font-bold">Not required</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile / tablet — card list */}
          <div className="lg:hidden grid sm:grid-cols-2 gap-3">
            {TABLE_ROWS.map((r) => (
              <article
                key={r.code}
                data-testid={`visa-card-${r.code}`}
                className="bg-white border border-slate-200 shadow-sm"
              >
                <div className={`h-1.5 bg-gradient-to-r ${r.accent}`} />
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">Subclass {r.code}</div>
                      <h3 className="font-display text-[15px] text-[#29324A] mt-0.5">{r.name}</h3>
                    </div>
                    <span className={`shrink-0 text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 ${r.pr ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                      {r.pr ? "PR" : "Temp"}
                    </span>
                  </div>
                  <div className="text-[11.5px] text-slate-500 mt-1">{r.type} · {r.location}</div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
                    <div><span className="text-slate-500">Fee:</span> <span className="text-[#C43A89] font-bold">{r.total}</span></div>
                    <div><span className="text-slate-500">Processing:</span> {r.processing}</div>
                    <div><span className="text-slate-500">Aged:</span> {r.aged}</div>
                    <div><span className="text-slate-500">BoF:</span> {r.bof ? "Required" : "—"}</div>
                  </div>
                  <p className="mt-3 pt-3 border-t border-slate-100 text-[12.5px] text-slate-700 leading-relaxed"><strong className="text-[#29324A]">Best for:</strong> {r.best}</p>
                </div>
              </article>
            ))}
          </div>

          {/* Recommendation strip below table */}
          <div className="mt-6 bg-white border-l-4 border-[#C43A89] p-5 flex flex-col md:flex-row items-start md:items-center gap-4">
            <Sparkles className="h-5 w-5 text-[#C43A89] shrink-0 mt-0.5 md:mt-0" />
            <p className="text-[13.5px] text-slate-700 flex-1 leading-relaxed">
              <strong className="text-[#29324A]">Not sure which subclass fits your family?</strong> Our Pathway Assessment runs the Balance of Family test, Aged Parent gate and budget checks in 3 minutes — then ranks all 8 subclasses for you.
            </p>
            <Link
              to="/parent-visa-finder"
              data-testid="hub-compare-cta"
              className="shrink-0 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-[11.5px] font-bold tracking-wider uppercase px-5 py-3 inline-flex items-center gap-2"
            >
              Start free assessment <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* THREE RULES */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Info className="h-3 w-3" /> Three rules that decide everything
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">Before you pick a visa — understand these three eligibility rules</h2>

          <div className="grid md:grid-cols-3 gap-4 mt-8">
            <div className="border border-slate-200 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Rule 1</div>
              <h3 className="font-display text-lg text-[#29324A] mt-2">Sponsorship</h3>
              <p className="text-[13px] text-slate-700 leading-relaxed mt-2">
                An Australian <strong>citizen</strong>, <strong>permanent resident</strong>, or <strong>eligible NZ citizen</strong> who is <em>settled</em> in Australia must agree to sponsor the parent. Joint sponsorship with a partner is sometimes permitted.
              </p>
            </div>
            <div className="border border-slate-200 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Rule 2</div>
              <h3 className="font-display text-lg text-[#29324A] mt-2">Balance of Family Test</h3>
              <p className="text-[13px] text-slate-700 leading-relaxed mt-2">
                At least <strong>half of the parent&apos;s children</strong> must live permanently in Australia — or an equal number in Australia compared to any other single country. Children include biological, adopted &amp; step-children. Fails? Consider 870 or 600.
              </p>
            </div>
            <div className="border border-slate-200 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89]">Rule 3</div>
              <h3 className="font-display text-lg text-[#29324A] mt-2">Aged Parent Rule</h3>
              <p className="text-[13px] text-slate-700 leading-relaxed mt-2">
                &ldquo;Aged Parent&rdquo; visas (Subclass 804, 864, 884) are only available if the parent has reached the current Australian <strong>Age Pension age (67 years)</strong>. Below that, use 103, 143, 173 or 870. Aged parents applying onshore get a <strong>Bridging Visa A</strong> during the wait.
              </p>
            </div>
          </div>

          {/* Assurance of Support explainer */}
          <div className="mt-8 bg-gradient-to-br from-[#F8F4FB] to-white border border-[#29324A]/20 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="shrink-0 h-14 w-14 rounded-full bg-[#29324A]/10 flex items-center justify-center">
                <ShieldCheck className="h-7 w-7 text-[#29324A]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Extra requirement for contributory visas</div>
                <h3 className="font-display text-xl text-[#29324A] mt-1">What is an Assurance of Support (AoS)?</h3>
                <p className="text-[13.5px] text-slate-700 leading-relaxed mt-2">
                  An AoS is a legal commitment made by a family member (the assurer) to repay certain Centrelink welfare payments if the parent uses them after arrival. It is <strong>required for all Contributory Parent Visas</strong> and is administered by Services Australia — separately from the visa application.
                </p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-1.5 text-[12.5px] text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Refundable bond held for 10 years</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Typically ~AUD 10,000 primary + AUD 4,000 per adult</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Assurer must meet a minimum income test</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Assurer doesn&apos;t have to be the sponsor</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAN PARENTS VISIT WHILE WAITING */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
            <Plane className="h-3 w-3" /> Bridging the wait
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">Can my parents visit Australia while their parent visa is being processed?</h2>
          <p className="text-[13.5px] text-slate-700 leading-relaxed mt-4 max-w-3xl">
            Yes — and for most families, this is essential. With waits of 15+ years, parents typically use either a Visitor Visa (600) for short trips or the Sponsored Parent Temporary Visa (870) for long continuous stays.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="bg-white border border-slate-200 p-6 md:p-7">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Short visits</div>
              <h3 className="font-display text-xl text-[#29324A] mt-2">Visitor Visa (Subclass 600) — Sponsored Family Stream</h3>
              <p className="text-[13.5px] text-slate-700 leading-relaxed mt-2">Up to 12 months per visit. Cheap (from AUD 250), fast (1–3 months), and specifically designed for family visits. Not a substitute for a parent visa — no PR, no Medicare, no work rights.</p>
            </div>
            <div className="bg-white border border-slate-200 p-6 md:p-7">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89]">Long temporary stay</div>
              <h3 className="font-display text-xl text-[#29324A] mt-2">Sponsored Parent Temporary Visa (Subclass 870)</h3>
              <p className="text-[13.5px] text-slate-700 leading-relaxed mt-2">Stay in Australia for 3 or 5 years at a time — up to <strong>10 years lifetime cap</strong>. Not capped or queued (~10 months processing). Sponsor must meet an annual income requirement. No PR pathway, no Medicare.</p>
            </div>
          </div>

          <div className="mt-6 border-l-4 border-amber-500 bg-amber-50 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
              <p className="text-[13px] text-amber-900 leading-relaxed">
                <strong>Bridging visa trap:</strong> If your parent is <strong>under 67</strong> and applies for a Parent Visa (103 or 143) while onshore, they are <strong>not</strong> granted a bridging visa and must depart before their visitor visa expires. Only parents aged 67+ applying onshore for 804 or 864 get a Bridging Visa A during processing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AFTER VISA GRANTED */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Heart className="h-3 w-3" /> After the visa is granted
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">What can your parents do once they have permanent residency?</h2>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {AFTER_GRANT_RIGHTS.map(({ icon: Icon, k, v }) => (
              <div key={k} className="bg-white border border-slate-200 p-6 hover:border-[#29324A] hover:shadow-md transition-all">
                <div className="h-10 w-10 rounded-full bg-[#F8F4FB] flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-[#29324A]" />
                </div>
                <h3 className="font-display text-base text-[#29324A]">{k}</h3>
                <p className="text-[12.5px] text-slate-600 leading-relaxed mt-1.5">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MYTHS */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <AlertTriangle className="h-3 w-3" /> Common mistakes
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">Misunderstandings we see every week</h2>
          <ul className="mt-6 space-y-3">
            {MYTHS.map((m) => (
              <li key={m} className="flex gap-3 bg-white border-l-4 border-[#C43A89] p-4 text-[13.5px] text-slate-700">
                <AlertTriangle className="h-4 w-4 text-[#C43A89] shrink-0 mt-0.5" /> {m}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
            <HelpCircle className="h-3 w-3" /> FAQ
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">Frequently asked questions</h2>
          <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
            {[
              { q: "Can I bring my parents to Australia permanently?", a: "Yes — if you meet sponsorship requirements and your parents pass the Balance of Family Test. Most parent visas lead to permanent residency (some through a two-stage temporary → permanent process). The Sponsored Parent 870 is temporary only." },
              { q: "How much does a parent visa actually cost?", a: "Non-contributory Parent (103) starts from AUD 7,345 per applicant (both instalments). Contributory (143 or 864) costs from about AUD 48,640 per applicant. Sponsored Parent 870 costs AUD 6,070 (3-yr) or AUD 12,140 (5-yr). Additional applicants (spouse, dependants) add further charges — plus health, police, biometrics and (for contributory) an Assurance of Support bond." },
              { q: "How long is the wait, really?", a: "For new applications lodged today: Contributory Parent (143/864) — approximately 15 years. Non-contributory Parent (103/804) — approximately 33 years. Sponsored Parent 870 — 90% of applications finalised within 10 months (not subject to the annual cap)." },
              { q: "What is the Balance of Family Test?", a: "At least half of the parent's children must be settled in Australia as citizens or PRs — OR more of their children must live permanently in Australia than in any other single country. Includes biological, adopted and step-children. Fails one of them? Skip to Subclass 870 or Visitor 600." },
              { q: "What is an Assurance of Support?", a: "A legal financial commitment by a family member (or organisation) to repay certain Centrelink payments the parent receives after arriving. Required for all Contributory Parent Visas. A refundable bond (~AUD 10,000+) is held with Centrelink for 10 years. Administered by Services Australia." },
              { q: "Can my parents stay in Australia while their visa is processed?", a: "Only aged parents (67+) applying onshore for Subclass 804 or 864 are granted a Bridging Visa A while the application is processed. Parents under 67 applying onshore for 103 or 143 do NOT get a bridging visa — they must depart before their visitor visa expires and wait offshore." },
              { q: "What's the difference between Subclass 103 and 143?", a: "Both are permanent parent visas for parents under 67. 103 (non-contributory) costs from AUD 7,345 but takes ~33 years. 143 (contributory) costs from AUD 48,640 but is processed in ~15 years and requires an Assurance of Support." },
              { q: "Can both parents apply together?", a: "Yes — both parents can be included in the same application. Each applicant must meet health and character requirements, and additional visa application charges apply per person." },
              { q: "What documents will we need?", a: "At minimum: current passports, proof of the parent–child relationship (birth certificates), the sponsor's Form 40, evidence the Balance of Family Test is satisfied, and (for contributory) Assurance of Support documents. Health examinations and police certificates are arranged only when the Department requests them — don't do these upfront." },
              { q: "Can permanent parent visa holders sponsor other family later?", a: "Yes — once granted, they hold the same rights as most Australian permanent residents, including the ability to sponsor eligible family members and eventually apply for citizenship once residence requirements are met." },
            ].map((f) => (
              <details key={f.q} className="py-4 group">
                <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                  <span className="font-display text-base text-[#29324A]">{f.q}</span>
                  <span className="text-[#C43A89] text-lg leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-[13.5px] text-slate-700 leading-relaxed mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden">
        <span aria-hidden className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 relative">
          <div className="grid md:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
                <Sparkles className="h-3 w-3" /> Not sure which visa fits?
              </span>
              <h2 className="font-display text-3xl md:text-4xl mt-3 leading-tight">Get a personalised Parent Visa recommendation in 3 minutes.</h2>
              <p className="text-[14px] text-slate-200 mt-4 leading-relaxed max-w-2xl">
                Our Pathway Assessment walks you through simple, plain-English questions — no migration jargon — then runs the Balance of Family test, Aged Parent test, budget and risk checks and ranks the visas most likely to suit your family.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/parent-visa-finder"
                data-testid="hub-bottom-cta"
                className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4"
              >
                Start Assessment <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Disclaimer />
    </div>
  );
}
